import { localizedStrings } from '@core/strings'
import React, { useMemo } from 'react'
import { Box, Text } from '@fundamentals'
import {
  AttachmentTypes,
  QuestionProps,
} from '@core/components/templateAnswersForm'
import { useWatch } from 'react-hook-form'
import CaptureSignature from '@common/CaptureSignature'
import { AttachmentInput } from './AttachmentInput'
import { Section } from '@common/Section'
import CommentsButton from '@modules/comments/CommentsButton'
import { useComments } from '@core/react-query/features/comments/hooks/CommentsContext'
import CommentItem from '@modules/comments/CommentItem'
import { Comment } from '@core/entities/comments'
import { QuestionWrapperBase } from '@elements/Questions/QuestionWrapperBase'
import WarningCard from '@modules/permitDetails/WarningCard'
import moment from 'moment/moment'
import {
  DOCUMENTS_ONLY,
  PHOTOS_ONLY,
} from '@core/entities/template/TemplateQuestion/types'

const QuestionWrapper: React.FC<QuestionProps> = ({
  groupIndex,
  questionIndex,
  questionNumber,
  answer,
  readOnly,
  projectId,
  questionsFormKey,
  warnings = [],
  submittedBy,
}) => {
  const { commentsBySubTypeId, commentsQuery } = useComments()

  const question =
    answer ||
    useWatch({
      name: `${questionsFormKey}.${groupIndex}.data.${questionIndex}`,
    })

  const attachmentTypes = useMemo(() => {
    if (question.questionType === 'DOCUMENT') {
      if (question.allowedMediaTypes.includes(DOCUMENTS_ONLY)) {
        return [AttachmentTypes.PDF]
      }

      if (question.allowedMediaTypes.includes(PHOTOS_ONLY)) {
        if (question.requirePhotoFromCamera) return []
        return [AttachmentTypes.IMAGE]
      }

      if (question.requirePhotoFromCamera) return [AttachmentTypes.PDF]

      return [AttachmentTypes.IMAGE, AttachmentTypes.PDF]
    }
    if (question.requiresProof === 'NOT_REQUIRED') return
    if (question.requireProofFromCamera) return

    return [AttachmentTypes.IMAGE, AttachmentTypes.PDF]
  }, [
    question.questionType,
    question.requireProofFromCamera,
    question.requiresProof,
    question.requirePhotoFromCamera,
  ])

  const attachments = useMemo(() => {
    return [
      ...(question.submissionProofs?.map((proof) => ({
        ...proof,
        type: 'image',
      })) || []),
      ...(question.submissionDocuments?.map((document) => ({
        ...document,
        type: 'application/pdf',
      })) || []),
    ]
  }, [attachmentTypes])

  const comments = commentsQuery && commentsBySubTypeId[question.id]
  const latestComment = comments?.[comments.length - 1]

  const attachmentRequired =
    (question.questionType === 'DOCUMENT' && question.required) ||
    question.requiresProof === 'REQUIRED'

  return (
    <QuestionWrapperBase
      groupIndex={groupIndex}
      questionNumber={questionNumber}
      questionIndex={questionIndex}
      attachmentTypes={attachmentTypes}
      attachments={attachments}
      question={question}
      readOnly={readOnly}
      actionButton={
        readOnly &&
        commentsQuery && (
          <CommentsButton
            subType='QUESTION'
            subTypeId={question.id}
            subTypeComponent={
              <QuestionWrapperBase
                groupIndex={groupIndex}
                questionNumber={questionNumber}
                questionIndex={questionIndex}
                question={question}
                readOnly
              />
            }
          />
        )
      }
    >
      {readOnly && (
        <>
          {(attachmentTypes || !!attachments.length) && (
            <AttachmentInput
              heading={localizedStrings.proofs}
              inputProps={{ value: attachments }}
              dataTest={`${question.id}-attachment-input`}
              readOnly
              mt={2}
              warnings={warnings}
              capturedByName={submittedBy?.getFullName()}
              projectId={projectId}
              required={attachmentRequired}
            />
          )}
          {warnings?.map((warning, key) => (
            <Box mt={2} key={key}>
              <WarningCard warning={warning} showFeedbackButton />
            </Box>
          ))}
          {(question.localSignature || question.signatureUrl) && (
            <>
              <CaptureSignature
                inputProps={{
                  value: question.localSignature
                    ? URL.createObjectURL(question.localSignature)
                    : question.signatureUrl,
                }}
                optional={question.requiresSignature === 'OPTIONAL'}
                dataTest={question.id}
                readOnly
              />
              {question.lastEdit && (
                <Text variant='body2' color='grey.600'>
                  {`${localizedStrings.signedOn} ${moment(
                    question.lastEdit?.editedOn,
                  ).format('DD/MM/YYYY HH:mm')}`}
                </Text>
              )}
            </>
          )}
          {question?.questionAdditionalInformation && (
            <Section
              label={localizedStrings.questionAdditionalInformation(
                question.requiresAdditionalInformation === 'OPTIONAL',
              )}
              data-test={`${question.id}-${'QUESTION_ADDITIONAL_INFORMATION'}`}
              sx={{ mt: 2 }}
            >
              <Text> {question?.questionAdditionalInformation}</Text>
            </Section>
          )}
        </>
      )}
      {latestComment && readOnly && (
        <Box mt={3}>
          <CommentItem comment={Comment.new(latestComment)} />
        </Box>
      )}
    </QuestionWrapperBase>
  )
}

export { QuestionWrapper }
